import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Paper, Typography, TableContainer, Table, TableBody, TableHead, TableCell, TableRow, Card, CardHeader, ToggleButton, ToggleButtonGroup, Collapse, Stack, IconButton } from '@mui/material';
import { BarChart, Dataset, ArrowForward, AdsClick, Facebook, LinkedIn, X, QrCode2, Share, Groups } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import renderPercentage from '../renderPercentage';
import EmailClicksChart from '../charts/EmailClicksChart';
import { useTheme } from '@mui/material/styles';
import { lighten } from '@mui/system';
import ChannelTotalButton from './ChannelTotalButton';
import { grey } from '@mui/material/colors';
import ReportsCardContent from './ReportsCardContent';
import { channelColours } from '../../theme/constants';
import TypedValue from '../../TypedValue';

const Clicks = ({ onClick, unique, total, selected, channelTotals, teamsCampaigns, qrCodes, allChannelTotal, socialActions, onChangeTab, noLinks = false }) => {
    const [isChartView, setIsChartView] = useState(true);
    const [tab, setTab] = useState('');
    const navigate = useNavigate();
    const theme = useTheme();
    const chartHeight = 350;

    const qrCodesTotals = channelTotals.find(e => e.type === 'qrcode');

    // note that teamsTotals does NOT include clicks
    const teamsTotals = channelTotals.find(e => e.type === 'teams');

    const hasTeams = Boolean(teamsCampaigns.length > 0);
    const hasQRCodes = Boolean(qrCodes.length > 0);

    const facebookActions = socialActions.find(e => e.type.toLowerCase() === 'facebook' && e.action.toLowerCase().includes('click'))?.total || 0;
    const twitterActions = socialActions.find(e => e.type.toLowerCase() === 'twitter' && e.action.toLowerCase().includes('click'))?.total || 0;
    const linkedinActions = socialActions.find(e => e.type.toLowerCase() === 'linkedin' && e.action.toLowerCase().includes('click'))?.total || 0;
    const shareActions = socialActions.find(e => e.type.toLowerCase() === 'share' && e.action.toLowerCase().includes('click'))?.total || 0;

    const teamsActions = socialActions.find(e => e.type.toLowerCase() === 'teams' && e.action.toLowerCase().includes('click'))?.total || 0;

    const handleChange = (event, value) => {
        value !== null && setIsChartView(value);
    };

    const handleToggleTab = theTabToToggle => {
        setTab(tab === theTabToToggle ? '' : theTabToToggle);
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardHeader
                sx={{
                    m: 0,
                    py: 1,
                    '& .MuiCardHeader-action': {
                        alignSelf: 'center'
                    }
                }}
                slotProps={{
                    title: {
                        variant: 'h6',
                        fontWeight: 'normal'
                    }
                }}
                avatar={<AdsClick />}
                title="Clicks"
                action={(
                    <ToggleButtonGroup
                        size="small"
                        color="primary"
                        value={isChartView}
                        disabled={noLinks}
                        exclusive
                        onChange={handleChange}
                        aria-label="View"
                        sx={{
                            mr: 1
                        }}
                    >
                        <ToggleButton value={false}><Dataset fontSize="inherit" /></ToggleButton>
                        <ToggleButton value={true}><BarChart fontSize="inherit" /></ToggleButton>
                    </ToggleButtonGroup>
                )}
            />
            {noLinks ? (
                <ReportsCardContent>
                    <Box sx={{ height: chartHeight }}>
                        <Typography variant="body1">No Links</Typography>
                    </Box>
                </ReportsCardContent>
            ) : isChartView ? (
                <EmailClicksChart
                    unique={unique}
                    total={total}
                    selected={selected}
                    teamsTotal={teamsActions}
                    qrCodesTotal={qrCodesTotals.clicks}
                    allChannelTotal={allChannelTotal}
                    facebook={facebookActions}
                    twitter={twitterActions}
                    linkedin={linkedinActions}
                    share={shareActions}
                    chartHeight={chartHeight}
                />
            ) : (
                <ReportsCardContent paddingBottom={0}>
                    <Stack spacing={2}>
                        <Paper variant="outlined" sx={{ backgroundColor: lighten(theme.palette.primary.main, 0.9) }}>
                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell variant="head">
                                                Total
                                            </TableCell>
                                            <TableCell align="right" width="30%">
                                                <Typography variant="h6" color="primary">
                                                    <TypedValue value={allChannelTotal} type="number" accuracy={0} />
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" width="20%">
                                                {renderPercentage(allChannelTotal, selected)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                        <Paper variant="outlined">
                            <TableContainer>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell variant="head">
                                                Email Total
                                            </TableCell>
                                            <TableCell align="right" width="30%">
                                                <Typography variant="h6" color="primary">
                                                    <TypedValue value={total} type="number" accuracy={0} />
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" width="20%">
                                                {renderPercentage(total, selected)}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow
                                            hover
                                            onClick={onClick}
                                        >
                                            <TableCell variant="head">
                                                Email Unique
                                                <IconButton size="small" sx={{ ml: 1 }}>
                                                    <ArrowForward fontSize="inherit" />
                                                </IconButton>
                                            </TableCell>
                                            <TableCell align="right" width="30%">
                                                <Typography variant="h6" color="primary">
                                                    <TypedValue value={unique} type="number" accuracy={0} />
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" width="20%">
                                                {renderPercentage(unique, selected)}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                        <Box>
                            <Grid container spacing={2}>
                                <Grid size={4}>
                                    <ChannelTotalButton
                                        onClick={() => handleToggleTab('qrcodes')}
                                        disabled={!hasQRCodes}
                                        value={qrCodesTotals.clicks}
                                        count={qrCodesTotals.count}
                                        text="QR Codes"
                                        tabShown={tab === 'qrcodes'}
                                        hasTab={true}
                                        icon={<QrCode2 sx={{ color: grey[600] }} fontSize="small" />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <ChannelTotalButton
                                        onClick={() => handleToggleTab('teams')}
                                        disabled={!hasTeams}
                                        value={teamsActions}
                                        count={teamsTotals.count}
                                        text={`Teams Message${teamsTotals.count !== 1 ? 's' : ''}`}
                                        tabShown={tab === 'teams'}
                                        hasTab={true}
                                        icon={<Groups sx={{ color: channelColours.teams }} fontSize="small" />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <ChannelTotalButton
                                        onClick={() => onChangeTab('Channels')}
                                        value={facebookActions}
                                        text="Facebook"
                                        tabShown={tab === 'facebook'}
                                        icon={<Facebook sx={{ color: channelColours.facebook }} fontSize="small" />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <ChannelTotalButton
                                        onClick={() => onChangeTab('Channels')}
                                        value={linkedinActions}
                                        text="LinkedIn"
                                        tabShown={tab === 'linkedin'}
                                        icon={<LinkedIn sx={{ color: channelColours.linkedin }} fontSize="small" />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <ChannelTotalButton
                                        onClick={() => onChangeTab('Channels')}
                                        value={twitterActions}
                                        text="X"
                                        tabShown={tab === 'twitter'}
                                        icon={<X sx={{ color: channelColours.x }} fontSize="small" />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <ChannelTotalButton
                                        onClick={() => onChangeTab('Channels')}
                                        value={shareActions}
                                        text="Share Link"
                                        tabShown={tab === 'share'}
                                        icon={<Share sx={{ color: grey[600] }} fontSize="small" />}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        <Box>
                            <Collapse in={Boolean(tab === 'qrcodes')}>
                                <Paper variant="outlined" sx={{ mb: 2 }}>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell width="70%">
                                                        QR Code Name
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        Total Clicks
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {qrCodes.map(e => (
                                                    <TableRow
                                                        key={e.id}
                                                        onClick={() => navigate(`/editor/qr/${e.id}`)}
                                                        hover
                                                    >
                                                        <TableCell width="70%">
                                                            {e.name}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {e.clicks}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Collapse>
                            <Collapse in={Boolean(tab === 'teams')}>
                                <Paper variant="outlined" sx={{ mb: 2 }}>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>
                                                        Teams Message
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {teamsCampaigns.map(e => (
                                                    <TableRow
                                                        key={e.id}
                                                    >
                                                        <TableCell>
                                                            {e.name}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Collapse>
                        </Box>
                    </Stack>
                </ReportsCardContent>
            )}
        </Card>
    );
};

export default Clicks;